import {
  Button,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";

const PopupForm = ({ onclose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    cmobile: "",
    password: "",
    plan: "",
    planCategory: "",
    validityDays: "",
    monthlyScanLimit: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If paid plan and selecting from dropdown
    if (name === "plan") {
      setFormData((prev) => ({
        ...prev,
        plan: value,
        planCategory: "",
        validityDays: "", // reset when plan changes
        monthlyScanLimit: "",
      }));
    } else if (name === "planCategory") {
      setFormData((prev) => ({
        ...prev,
        planCategory: value,
        validityDays: "",
        monthlyScanLimit: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Company name is required.";
    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email format.";
    }
    if (!formData.cmobile) {
      newErrors.cmobile = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(formData.cmobile)) {
      newErrors.cmobile = "Mobile number must be 10 digits.";
    }
    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
    }
    if (formData.plan === "paid" && !formData.planCategory) {
      newErrors.planCategory = "Plan category is required for paid plans.";
    }

    if (
      (formData.plan === "trial" ||
        (formData.plan === "paid" && formData.planCategory === "basic")) &&
      !formData.monthlyScanLimit
    ) {
      newErrors.monthlyScanLimit = "Scan limit is required.";
    } else if (
      (formData.plan === "trial" ||
        (formData.plan === "paid" && formData.planCategory === "basic")) &&
      formData.monthlyScanLimit !== "50" &&
      !["50", "200"].includes(formData.monthlyScanLimit) &&
      (isNaN(formData.monthlyScanLimit) ||
        Number(formData.monthlyScanLimit) <= 0)
    ) {
      newErrors.monthlyScanLimit = "Scan limit must be a positive number.";
    }

    if (!formData.validityDays || formData.validityDays === "other") {
      newErrors.validityDays = "Validity days is required.";
    } else if (
      isNaN(formData.validityDays) ||
      Number(formData.validityDays) <= 0
    ) {
      newErrors.validityDays = "Validity must be a positive number.";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Create form data for file upload
    const formDataToSend = new FormData();
    formDataToSend.append("name", formData.name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("cmobile", formData.cmobile);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("remark", formData.remark);
    formDataToSend.append("plan", formData.plan);
    if (formData.plan === "paid") {
      formDataToSend.append("planCategory", formData.planCategory);
    }
    if (
      formData.plan === "trial" ||
      (formData.plan === "paid" && formData.planCategory === "basic")
    ) {
      formDataToSend.append("monthlyScanLimit", formData.monthlyScanLimit);
    }
    formDataToSend.append("validityDays", formData.validityDays);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/signup`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        alert("Company created successfully!");
        setFormData({
          name: "",
          email: "",
          cmobile: "",
          password: "",
          remark: "",
          plan: "",
          planCategory: "",
          validityDays: "",
          monthlyScanLimit: "",
        });
        onclose();
      } else {
        alert("Failed to create company.");
      }
    } catch (error) {
      console.error("Error creating company:", error);
      alert("Error creating company: " + error.response.data.error);
    }
    console.log("Form Data Submitted:", formData);
  };

  return (
    <DialogContent>
      <Typography variant="h5" align="center" gutterTop fontWeight="bold">
        Company Details
      </Typography>

      <Divider sx={{ mb: 2, height: 1, backgroundColor: "black" }} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Company Name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Company Mobile No."
              name="cmobile"
              type="tel"
              value={formData.cmobile}
              onChange={handleChange}
              error={!!errors.cmobile}
              helperText={errors.cmobile}
              inputProps={{
                maxLength: 10,
                inputMode: "numeric",
                pattern: "[6-9][0-9]{9}",
              }}
              onInput={(e) => {
                e.target.value = e.target.value
                  .replace(/[^0-9]/g, "")
                  .slice(0, 10);
              }}
              required
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              margin="normal"
              label="Company Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* Plan Type Selector */}
          <Grid item xs={6}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={!!errors.plan}
            >
              <InputLabel>Select Plan</InputLabel>
              <Select
                label="Select Plan"
                name="plan"
                value={formData.plan}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    plan: val,
                    planCategory: "", // reset on change
                    validityDays: "", // reset on change
                  }));
                }}
                autoComplete="off"
              >
                <MenuItem value="trial">Free</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
              </Select>
              {errors.plan && <FormHelperText>{errors.plan}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Conditional Rendered Plan Details */}
          <Grid item xs={6}>
            {formData.plan === "trial" ? (
              <>
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.validityDays}
                >
                  <InputLabel>Select Validity</InputLabel>
                  <Select
                    label="Select Validity"
                    value={
                      ["30"].includes(formData.validityDays)
                        ? formData.validityDays
                        : "custom"
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        validityDays: val === "custom" ? "" : val,
                      }));
                    }}
                  >
                    <MenuItem value="30">1 Month</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                  {errors.validityDays && (
                    <FormHelperText>{errors.validityDays}</FormHelperText>
                  )}
                </FormControl>

                {formData.validityDays !== "30" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Enter Validity Days"
                    name="validityDays"
                    type="number"
                    value={formData.validityDays}
                    onChange={handleChange}
                    error={!!errors.validityDays}
                    helperText={errors.validityDays}
                    required
                  />
                )}
              </>
            ) : (
              <>
                {/* Paid Plan Category Selector */}
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.planCategory}
                >
                  <InputLabel>Select Plan Category</InputLabel>
                  <Select
                    label="Select Plan Category"
                    name="planCategory"
                    value={formData.planCategory}
                    onChange={handleChange}
                    autoComplete="off"
                  >
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="pro">Pro</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                  {errors.planCategory && (
                    <FormHelperText>{errors.planCategory}</FormHelperText>
                  )}
                </FormControl>

                {/* Paid Plan Validity Selector */}
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={!!errors.validityDays}
                >
                  <InputLabel>Select Validity</InputLabel>
                  <Select
                    label="Select Validity"
                    value={formData.validityDays === "365" ? "365" : "custom"}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        validityDays: val === "custom" ? "" : val,
                      }));
                    }}
                  >
                    <MenuItem value="365">1 Year</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                  {errors.validityDays && (
                    <FormHelperText>{errors.validityDays}</FormHelperText>
                  )}
                </FormControl>

                {formData.validityDays !== "365" && (
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Enter Validity Days"
                    name="validityDays"
                    type="number"
                    value={formData.validityDays}
                    onChange={handleChange}
                    error={!!errors.validityDays}
                    helperText={errors.validityDays}
                    required
                  />
                )}
              </>
            )}
          </Grid>
        </Grid>

        {(formData.plan === "trial" ||
          (formData.plan === "paid" && formData.planCategory === "basic")) && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl
                fullWidth
                margin="normal"
                required
                error={!!errors.monthlyScanLimit}
              >
                <InputLabel>Select Scan Limit</InputLabel>
                <Select
                  label="Select Scan Limit"
                  value={
                    [
                      formData.plan === "paid" &&
                      formData.planCategory === "basic"
                        ? "200"
                        : "50",
                    ].includes(formData.monthlyScanLimit)
                      ? formData.monthlyScanLimit
                      : "custom"
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      monthlyScanLimit: val === "custom" ? "" : val,
                    }));
                  }}
                >
                  {/* Show 50 or 200 depending on the plan */}
                  {formData.plan === "paid" &&
                  formData.planCategory === "basic" ? (
                    <MenuItem value="200">200 Scans</MenuItem>
                  ) : (
                    <MenuItem value="50">50 Scans</MenuItem>
                  )}
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
                {errors.monthlyScanLimit && (
                  <FormHelperText>{errors.monthlyScanLimit}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {formData.monthlyScanLimit !==
              (formData.plan === "paid" && formData.planCategory === "basic"
                ? "200"
                : "50") && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Enter Scan Limit"
                  name="monthlyScanLimit"
                  type="number"
                  value={formData.monthlyScanLimit}
                  onChange={handleChange}
                  error={!!errors.monthlyScanLimit}
                  helperText={errors.monthlyScanLimit}
                  required
                />
              </Grid>
            )}
          </Grid>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              margin="normal"
              label="Admin Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              autoComplete="new-password"
              required
            />
          </Grid>
        </Grid>

        <div style={{ marginTop: "10px" }}>
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Submit
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default PopupForm;
